import { useState, useCallback, useRef, useEffect } from 'react';
import { MIN_DURATION_MINUTES } from '../../../../utils/taskDuration';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_MINUTE = 1000 * 60;

/**
 * useGanttBarDrag Hook
 *
 * Handles all drag-related logic for GanttBar:
 * - Move (drag entire bar) - changes startedAt
 * - Resize from start - changes startedAt + estimated duration (minutes)
 * - Resize from end - changes estimated duration (minutes)
 * - Move due diamond - changes dueDate
 * - Multi-drag support for selected bars
 * - Snap to zoom level units
 */
export const useGanttBarDrag = ({
  taskId,
  startDate,
  endDate,
  dueDate,
  hasEstimate,
  viewStartDate,
  totalDays,
  baseLeftPercent,
  baseWidthPercent,
  zoomLevel = 'day',
  isSelected = false,
  selectedTaskIds,
  onDateChange,
  onDueDateChange,
  onMultiDrag,
  isDraggingMultiple = false,
  multiDragDelta = 0,
  multiDragType = null,
  barRef,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null);

  const [dragLeft, setDragLeft] = useState(null);
  const [dragWidth, setDragWidth] = useState(null);

  const hasDraggedRef = useRef(false);

  const dragStartRef = useRef({
    x: 0,
    startDate: null,
    endDate: null,
    dueDate: null,
    initialLeft: 0,
    initialWidth: 0,
    timelineWidth: 0,
  });

  const minWidthPercent = (1 / (totalDays * 1440)) * MIN_DURATION_MINUTES * 100;

  let leftPercent;
  let widthPercent;

  if (dragLeft !== null) {
    leftPercent = dragLeft;
    widthPercent = dragWidth !== null ? dragWidth : baseWidthPercent;
  } else if (isSelected && isDraggingMultiple) {
    if (multiDragType === 'move') {
      leftPercent = baseLeftPercent + multiDragDelta;
      widthPercent = baseWidthPercent;
    } else if (multiDragType === 'resize-start') {
      const newLeft = baseLeftPercent + multiDragDelta;
      const newWidth = baseWidthPercent - multiDragDelta;
      if (newWidth >= minWidthPercent) {
        leftPercent = newLeft;
        widthPercent = newWidth;
      } else {
        leftPercent = baseLeftPercent;
        widthPercent = baseWidthPercent;
      }
    } else if (multiDragType === 'resize-end') {
      leftPercent = baseLeftPercent;
      const newWidth = baseWidthPercent + multiDragDelta;
      widthPercent = newWidth >= minWidthPercent ? newWidth : baseWidthPercent;
    } else {
      leftPercent = baseLeftPercent;
      widthPercent = baseWidthPercent;
    }
  } else {
    leftPercent = baseLeftPercent;
    widthPercent = baseWidthPercent;
  }

  const pixelsToPercent = useCallback((pixelDelta) => {
    const timelineWidth = dragStartRef.current.timelineWidth;
    if (!timelineWidth) return 0;
    return (pixelDelta / timelineWidth) * 100;
  }, []);

  const getSnapUnit = useCallback(() => {
    switch (zoomLevel) {
      case 'hour-detail':
      case 'hour':
        return 1 / 24;
      case 'day-detail':
      case 'day':
        return 1;
      case 'week':
        return 1;
      case 'month':
      case 'quarter':
      case 'year':
        return 1;
      default:
        return 1;
    }
  }, [zoomLevel]);

  /** Snapped delta in whole minutes */
  const percentToMinutes = useCallback((percentDelta) => {
    const rawDays = (percentDelta / 100) * totalDays;
    const snapUnit = getSnapUnit();
    const snappedDays = Math.round(rawDays / snapUnit) * snapUnit;
    return Math.round(snappedDays * 1440);
  }, [totalDays, getSnapUnit]);

  const handleMouseDown = useCallback((e, type) => {
    if (e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();

    if (type === 'move-due' && !onDueDateChange) return;
    if (type !== 'move-due' && !onDateChange) return;

    const timeline = barRef.current?.parentElement;
    if (!timeline) return;

    setIsDragging(true);
    setDragType(type);
    setDragLeft(baseLeftPercent);
    setDragWidth(baseWidthPercent);
    hasDraggedRef.current = false;

    dragStartRef.current = {
      x: e.clientX,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      dueDate: dueDate ? new Date(dueDate) : null,
      initialLeft: baseLeftPercent,
      initialWidth: baseWidthPercent,
      timelineWidth: timeline.offsetWidth,
    };

    document.body.style.cursor = type === 'move' ? 'grabbing' : 'ew-resize';
  }, [onDateChange, onDueDateChange, startDate, endDate, dueDate, baseLeftPercent, baseWidthPercent, barRef]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaPercent = pixelsToPercent(deltaX);

      if (Math.abs(deltaX) > 3) {
        hasDraggedRef.current = true;
      }

      const { initialLeft, initialWidth } = dragStartRef.current;

      const isMultiSelected = isSelected && selectedTaskIds && selectedTaskIds.size > 1;

      if (dragType === 'move') {
        setDragLeft(initialLeft + deltaPercent);

        if (isMultiSelected && onMultiDrag) {
          onMultiDrag(deltaPercent, false, 0, 'move');
        }
      } else if (dragType === 'resize-start') {
        const newLeft = initialLeft + deltaPercent;
        const newWidth = initialWidth - deltaPercent;
        if (newWidth >= minWidthPercent) {
          setDragLeft(newLeft);
          setDragWidth(newWidth);

          if (isMultiSelected && onMultiDrag) {
            onMultiDrag(deltaPercent, false, 0, 'resize-start');
          }
        }
      } else if (dragType === 'resize-end') {
        const newWidth = initialWidth + deltaPercent;
        if (newWidth >= minWidthPercent) {
          setDragWidth(newWidth);

          if (isMultiSelected && onMultiDrag) {
            onMultiDrag(deltaPercent, false, 0, 'resize-end');
          }
        }
      }
    };

    const handleMouseUp = (e) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaPercent = pixelsToPercent(deltaX);
      const deltaMinutes = percentToMinutes(deltaPercent);

      if (hasDraggedRef.current) {
        const { startDate: origStart, dueDate: origDue } = dragStartRef.current;

        const isMultiSelected = isSelected && selectedTaskIds && selectedTaskIds.size > 1;

        if (dragType === 'move') {
          const newStart = new Date(origStart.getTime() + deltaMinutes * MS_PER_MINUTE);

          if (isMultiSelected && onMultiDrag) {
            onMultiDrag(deltaPercent, true, deltaMinutes, 'move');
          } else {
            onDateChange?.(taskId, newStart, deltaMinutes, 'move');
          }
        } else if (dragType === 'resize-start') {
          const newStart = new Date(origStart.getTime() + deltaMinutes * MS_PER_MINUTE);

          if (isMultiSelected && onMultiDrag) {
            onMultiDrag(deltaPercent, true, deltaMinutes, 'resize-start');
          } else {
            onDateChange?.(taskId, newStart, deltaMinutes, 'resize-start');
          }
        } else if (dragType === 'resize-end') {
          const currentDurationMs = dragStartRef.current.endDate - origStart;
          const currentDurationMinutes = Math.max(
            MIN_DURATION_MINUTES,
            Math.round(currentDurationMs / MS_PER_MINUTE),
          );
          const newDurationMinutes = Math.max(
            MIN_DURATION_MINUTES,
            currentDurationMinutes + deltaMinutes,
          );

          if (isMultiSelected && onMultiDrag) {
            onMultiDrag(deltaPercent, true, deltaMinutes, 'resize-end');
          } else {
            onDateChange?.(taskId, null, newDurationMinutes, 'resize-end');
          }
        } else if (dragType === 'move-due') {
          if (origDue) {
            // Preserve time-of-day; shift by drag delta in minutes (handler persists full ISO).
            const newDue = new Date(origDue.getTime() + deltaMinutes * MS_PER_MINUTE);
            onDueDateChange?.(taskId, newDue);
          }
        }
      }

      setIsDragging(false);
      setDragType(null);
      setDragLeft(null);
      setDragWidth(null);
      document.body.style.cursor = '';

      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 0);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };
  }, [isDragging, dragType, pixelsToPercent, percentToMinutes, taskId, onDateChange, onDueDateChange, totalDays, isSelected, selectedTaskIds, onMultiDrag, minWidthPercent]);

  const isMultiDragging = isSelected && isDraggingMultiple && !isDragging;

  return {
    isDragging,
    isMultiDragging,
    leftPercent,
    widthPercent,
    hasDraggedRef,
    handleMouseDown,
  };
};

export default useGanttBarDrag;
